/**
 * Manual Assets list (AST-115)
 *
 * Browse AsseTrac's global asset types (left) and the selected type's
 * subtypes (right), each with its count of ACTIVE manual assets and one
 * "Manage Manual Assets ›" link that opens the modal (AST-116). Search finds
 * a manual asset by name or description across every subtype.
 *
 * Types and subtypes are global and read-only here (LIST-5, LIST-8, LIST-18).
 * Only the counts and the manual assets themselves are per-customer, and the
 * server limits those to the current customer (LIST-31).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, ClickAwayListener, InputAdornment, List, ListItemButton,
  Paper, Popper, Skeleton, Snackbar, TextField, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import SpriteIcon from '../elements/SpriteIcon';
import ManageManualAssetsModal from '../elements/ManageManualAssetsModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAssetTypes, fetchAssetSubTypes } from '../../store/slices/assetSlice';
import { fetchManualAssetCounts, searchManualAssets, manualAssetErrorMessage } from '../../services/manualAssetsApi';
import { getSubtypeIconWithFallback, getSubtypeColorWithFallback } from '../../utils/assetTypeResolution';
import { useCanManageManualAssets } from '../../hooks/useCanManageManualAssets';
import AppBar from '../elements/AppBar';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import useProjectSelector from '../../hooks/useProjectSelector';

const ACCENT = '#EC6825'; // asseTracOrange
const LAST_TYPE_KEY = 'manualAssets.lastTypeId'; // LIST-10, per session
const SEARCH_DEBOUNCE_MS = 300;

export const countLabel = (n) => `${n} manual asset${n === 1 ? '' : 's'}`;

/** Access denied (AST-114 NAV-12 / AST-115 AC-L9): no data is loaded. */
export function ManualAssetsAccessDenied() {
  const navigate = useNavigate();
  return (
    <PageBody>
      <Box role="alert" sx={{ maxWidth: 520, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>You don&apos;t have access to Manual Assets</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Manual assets are managed by your organization&apos;s administrators.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>Go to home</Button>
      </Box>
    </PageBody>
  );
}

export default function ManualAssetsPage() {
  const { allowed, customer } = useCanManageManualAssets();
  // AST-124/125: the same AppBar (and project selector) every other page
  // has -- this page shipped without one. Rendered for the denied state too,
  // so a user who lands here can still navigate away.
  const { ProjectSelectorComponent } = useProjectSelector();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar showProjectSelector projectSelectorProps={{ ProjectSelectorComponent }} />
      {/* Gate before any hook that fetches, so a denied user triggers no requests. */}
      {allowed ? <ManualAssetsList customer={customer} /> : <ManualAssetsAccessDenied />}
    </Box>
  );
}

function ManualAssetsList({ customer }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { assetTypes, assetSubTypes } = useAppSelector((s) => s.asset);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [counts, setCounts] = useState(null);
  const [countsError, setCountsError] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(() => sessionStorage.getItem(LAST_TYPE_KEY));
  const [modal, setModal] = useState(null); // { assetType, subtype, highlightId }
  const [toast, setToast] = useState(null);
  const subtypeRefs = useRef({});

  // Stable identity: `assetTypes || []` would be a new array every render and
  // keep re-running the effects below.
  const types = useMemo(() => assetTypes || [], [assetTypes]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      await Promise.all([dispatch(fetchAssetTypes()).unwrap(), dispatch(fetchAssetSubTypes()).unwrap()]);
    } catch (err) {
      // fetchAssetTypes/fetchAssetSubTypes rejectWithValue(error.message) -- a string.
      setCatalogError(typeof err === 'string' ? err : manualAssetErrorMessage(err, "Couldn't load asset types."));
    } finally {
      setCatalogLoading(false);
    }
  }, [dispatch]);

  const loadCounts = useCallback(async () => {
    setCountsError(null);
    try {
      const res = await fetchManualAssetCounts(customer.id);
      setCounts(res.counts || {});
    } catch (err) {
      setCountsError(manualAssetErrorMessage(err, "Couldn't load manual asset counts."));
    }
  }, [customer.id]);

  // LIST-19: always refetch the global catalog on load, so a subtype AsseTrac
  // just added shows up (with 0) without relying on a stale cache.
  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  // Re-run when the nav drawer's customer changes (US-L5).
  useEffect(() => { setCounts(null); loadCounts(); }, [loadCounts]);

  // LIST-10: first type on first load; restore the last one this session if
  // it still exists.
  useEffect(() => {
    if (!types.length) return;
    if (!selectedTypeId || !types.some((t) => t.id === selectedTypeId)) setSelectedTypeId(types[0].id);
  }, [types, selectedTypeId]);
  useEffect(() => { if (selectedTypeId) sessionStorage.setItem(LAST_TYPE_KEY, selectedTypeId); }, [selectedTypeId]);

  const selectedType = types.find((t) => t.id === selectedTypeId) || null;
  // LIST-13: global-list order -- the order the API returns, as Global Asset Types shows it.
  const subtypes = useMemo(
    () => (assetSubTypes || []).filter((s) => s.typeId === selectedTypeId),
    [assetSubTypes, selectedTypeId]
  );

  const typeById = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);
  const subtypeById = useMemo(() => new Map((assetSubTypes || []).map((s) => [s.id, s])), [assetSubTypes]);

  const openModal = (assetType, subtype, highlightId = null) => setModal({ assetType, subtype, highlightId });

  // LIST-25: select the type, scroll to the subtype, open the modal on that row.
  const openFromSearch = (hit) => {
    const sub = subtypeById.get(hit.subtypeId);
    const type = typeById.get(hit.assetTypeId) || (sub && typeById.get(sub.typeId));
    if (!sub || !type) return;
    setSelectedTypeId(type.id);
    setTimeout(() => subtypeRefs.current[sub.id]?.scrollIntoView?.({ block: 'center' }), 0);
    openModal(type, sub, hit.id);
  };

  const loading = catalogLoading && !types.length; // LIST-27

  return (
    <PageBody>
      {/* AST-124 BN-1..3: "‹ Home", same component, style and spacing as
          the Project List screen's (ProjectListPage). */}
      <Button
        variant="text"
        color="secondary"
        startIcon={<ChevronLeftIcon />}
        onClick={() => navigate('/')}
        sx={{ height: 44, mb: 2 }}
      >
        Home
      </Button>

      {/* LIST-1..4. No Add button, by design (LIST-3). */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: 640 }}>
          <PageTitle>Manual Assets</PageTitle>
          <Typography variant="body2" color="text.secondary">
            The mats, equipment and materials your organization counts on manually tracked projects. Each one sits under an AsseTrac subtype.
          </Typography>
        </Box>
        <ManualAssetSearch customerId={customer.id} typeById={typeById} subtypeById={subtypeById} onPick={openFromSearch} />
      </Box>

      {(catalogError || countsError) && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => { if (catalogError) loadCatalog(); if (countsError) loadCounts(); }}>
              Retry
            </Button>
          )}
        >
          {catalogError || countsError}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 3, mt: 3, alignItems: 'start' }}>
        {/* Asset Types panel -- LIST-5..11 */}
        <Paper variant="outlined" sx={{ maxHeight: { md: 'calc(100vh - 240px)' }, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1, flexGrow: 1 }}>ASSET TYPES</Typography>
            <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-label="Read-only" titleAccess="Read-only" />
          </Box>
          {loading ? (
            <Box sx={{ p: 2 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={40} />)}</Box>
          ) : (
            <List disablePadding aria-label="Asset types">
              {types.map((t) => {
                const selected = t.id === selectedTypeId;
                return (
                  <ListItemButton
                    key={t.id}
                    selected={selected}
                    onClick={() => setSelectedTypeId(t.id)}
                    aria-current={selected ? 'true' : undefined}
                    sx={{ gap: 1.5, py: 1.25, borderLeft: '3px solid', borderLeftColor: selected ? ACCENT : 'transparent' }}
                  >
                    <TypeIcon iconName={t.iconName} color={t.color} />
                    <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>{t.name}</Typography>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>

        {/* Subtypes panel -- LIST-12..20, LIST-28 */}
        <Paper variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
              {selectedType ? `${selectedType.name} · Subtypes` : 'Subtypes'}
            </Typography>
            <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">Subtypes defined by AsseTrac</Typography>
          </Box>
          {loading ? (
            <Box sx={{ p: 2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={56} />)}</Box>
          ) : subtypes.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 3 }}>No subtypes are defined for this asset type.</Typography>
          ) : (
            <Box role="list" aria-label={selectedType ? `${selectedType.name} subtypes` : 'Subtypes'}>
              {subtypes.map((s) => {
                const n = counts ? (counts[s.id] || 0) : null; // LIST-19: absent = 0
                return (
                  <Box
                    key={s.id}
                    role="listitem"
                    ref={(el) => { subtypeRefs.current[s.id] = el; }}
                    data-testid={`subtype-row-${s.id}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <TypeIcon
                      iconName={getSubtypeIconWithFallback(s, selectedType)}
                      color={getSubtypeColorWithFallback(s, selectedType)}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary" data-testid={`subtype-count-${s.id}`}>
                        {n === null ? (countsError ? '—' : 'Loading…') : countLabel(n)}
                      </Typography>
                    </Box>
                    {/* LIST-16: one action, identical whatever the count */}
                    <Button
                      endIcon={<ChevronRightIcon />}
                      onClick={() => openModal(selectedType, s)}
                      aria-label={`Manage Manual Assets for ${s.name}`}
                      sx={{ textTransform: 'none', color: ACCENT }}
                    >
                      Manage Manual Assets
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>

      {modal && (
        <ManageManualAssetsModal
          open
          customerId={customer.id}
          assetType={modal.assetType}
          subtype={modal.subtype}
          highlightId={modal.highlightId}
          onClose={() => setModal(null)}
          onSaved={(name) => {
            setToast(`Manual assets saved for ${name}`); // MOD-33
            loadCounts(); // LIST-20: count updates without a page reload
          }}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </PageBody>
  );
}

function TypeIcon({ iconName, color }) {
  return (
    <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: color || ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {iconName && <SpriteIcon iconName={iconName} size={22} />}
    </Box>
  );
}

/** LIST-21..26 */
function ManualAssetSearch({ customerId, typeById, subtypeById, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null); // null = not searched
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setError(null); return undefined; } // LIST-22
    const mine = (seq.current += 1);
    const t = setTimeout(async () => {
      try {
        const res = await searchManualAssets(customerId, term);
        if (mine === seq.current) { setResults(res.items || []); setError(null); setOpen(true); }
      } catch (err) {
        if (mine === seq.current) { setError(manualAssetErrorMessage(err, "Search isn't available right now.")); setOpen(true); }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, customerId]);

  const breadcrumb = (hit) => {
    const sub = subtypeById.get(hit.subtypeId);
    const type = typeById.get(hit.assetTypeId) || (sub && typeById.get(sub.typeId));
    return [type?.name, sub?.name].filter(Boolean).join(' › ');
  };

  const pick = (hit) => { setOpen(false); onPick(hit); };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchor} sx={{ width: { xs: '100%', sm: 340 } }}>
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results || error) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          placeholder="Search manual assets (e.g. ST124)"
          size="small"
          fullWidth
          inputProps={{ 'aria-label': 'Search manual assets' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {/* Children render even while the Popper is closed, so nothing here
            may assume `results` is set -- it starts as null (not searched). */}
        <Popper open={open && (!!results || !!error)} anchorEl={anchor.current} placement="bottom-end" sx={{ zIndex: 1300, width: anchor.current?.offsetWidth }}>
          <Paper elevation={6} sx={{ mt: 0.5, maxHeight: 420, overflowY: 'auto' }} role="listbox" aria-label="Manual asset search results">
            {error ? (
              <Typography color="error" sx={{ p: 2 }} variant="body2">{error}</Typography>
            ) : !results ? null : results.length === 0 ? (
              <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">No manual assets match &apos;{q.trim()}&apos;.</Typography>
            ) : (
              results.map((hit) => (
                <ListItemButton key={hit.id} role="option" onClick={() => pick(hit)} sx={{ display: 'block', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{hit.name}</Typography>
                    {hit.isArchived && <Chip label="Archived" size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
                  </Box>
                  {hit.description && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{hit.description}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" component="div" data-testid="search-breadcrumb">{breadcrumb(hit)}</Typography>
                </ListItemButton>
              ))
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
